package org.example.project.screens_sys

import androidx.compose.runtime.*
import kotlinx.coroutines.delay
import org.example.project.dashboard.DashboardTheme
import org.example.project.enginetabular.TabularScreen
import org.example.project.enginetabular.TabularSplitScreen
import org.example.project.manager.SystemNodeInfo
import org.example.project.service.SystemBuildApi
import org.example.project.utils.All
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Text

private data class VerwaltungJournal(
    val id: String,
    val date: String,
    val time: String,
    val type: String,
    val version: String,
    val user: String
)

private object SystemverwaltungStyles : StyleSheet() {
    val screenRoot by style {
        width(100.percent)
        height(100.percent)
        minHeight(0.px)
        padding(18.px)
        boxSizing("border-box")
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(14.px)
    }
    val title by style {
        color(DashboardTheme.TEXT_LIGHT)
        fontSize(22.px)
        fontWeight("750")
        textAlign("center")
    }
    val tableArea by style {
        flex(1)
        minHeight(620.px)
        width(100.percent)
    }
}

@Composable
fun SysBuildUebersichtScreen() {
    Style(SystemverwaltungStyles)
    var builds by remember { mutableStateOf(emptyList<All>()) }
    var refresh by remember { mutableStateOf(0) }
    var selectedVersion by remember { mutableStateOf("") }
    var details by remember { mutableStateOf(emptyList<All>()) }

    LaunchedEffect(refresh) {
        builds = SystemBuildApi.load()
            .filter { it.buildDatensatzTyp == "BUILD" && it.buildUmgebung == "build" }
            .filter { it.buildVersion.isNotBlank() }
            .distinctBy { it.buildVersion }
    }
    LaunchedEffect(selectedVersion) {
        details = if (selectedVersion.isBlank()) emptyList() else SystemBuildApi.details(selectedVersion)
            .filter { it.buildDatensatzTyp == "DETAIL_NOTE" }
    }

    val headers = listOf("Version", "Für LIVE freigegeben", "Erstellt am", "Erstellt um", "Erstellt von")
    val detailHeaders = listOf("Datum", "Benutzer", "Inhalt")
    Div({ classes(SystemverwaltungStyles.screenRoot) }) {
        Div({ classes(SystemverwaltungStyles.title) }) { Text("Buildübersicht") }
        Div({ classes(SystemverwaltungStyles.tableArea) }) {
            TabularSplitScreen(
                initialRatio = 0.45f,
                minRatio = 0.25f,
                maxRatio = 0.75f,
                screen1 = {
            Div({ style { width(100.percent); height(100.percent); minHeight(0.px) } }) {
            TabularScreen(
                screenKey = "SysBuildUebersicht",
                rollenKey = "SystemBuildUebersicht",
                title = "Buildübersicht",
                headers = headers,
                data = builds.map { build ->
                    build.buildVersion to listOf(
                        build.buildVersion,
                        if (build.buildLiveFreigegeben) "Ja" else "Nein",
                        build.erstelltAm, build.erstelltUm, build.erstelltVon
                    )
                },
                editable = false,
                hardReadOnly = true,
                includeMetadata = false,
                smartMode = false,
                nonEditableCols = headers.toSet(),
                allowCreate = false,
                allowSave = false,
                allowDelete = false,
                allowImportButton = false,
                allowExportButton = true,
                onRowClick = { selectedVersion = it },
                onRefresh = { refresh++ },
                onCommit = {}
            )
            }
                },
                screen2 = {
            Div({ style { width(100.percent); height(100.percent); minHeight(0.px) } }) {
                if (selectedVersion.isNotBlank()) {
                    TabularScreen(
                        screenKey = "SysBuildUebersichtDetails",
                        rollenKey = "SystemBuildUebersicht",
                        title = "Build-Info V $selectedVersion",
                        headers = detailHeaders,
                        data = details.mapIndexed { index, row ->
                            "${row.id}_$index" to listOf(
                                "${row.erstelltAm} ${row.erstelltUm}".trim(),
                                row.erstelltVon,
                                row.buildKommentar.ifBlank { row.buildWert }
                            )
                        },
                        editable = false,
                        hardReadOnly = true,
                        includeMetadata = false,
                        smartMode = false,
                        nonEditableCols = detailHeaders.toSet(),
                        allowCreate = false,
                        allowSave = false,
                        allowDelete = false,
                        allowImportButton = false,
                        allowExportButton = true,
                        onCommit = {}
                    )
                }
            }
                }
            )
        }
    }
}

private suspend fun loadJournal(): List<VerwaltungJournal> =
    SystemBuildApi.load()
        .asSequence()
        .filter { it.buildDatensatzTyp == "JOURNAL" }
        .filterNot { it.buildUmgebung.equals("test", ignoreCase = true) }
        .filterNot { it.buildEreignisTyp.contains("test", ignoreCase = true) }
        .map {
            VerwaltungJournal(
                id = it.id,
                date = it.erstelltAm,
                time = it.erstelltUm,
                type = it.buildEreignisTyp,
                version = it.buildVersion,
                user = it.erstelltVon
            )
        }
        .toList()

@Composable
fun SysBuildJournalScreen() {
    Style(SystemverwaltungStyles)
    var journal by remember { mutableStateOf(emptyList<VerwaltungJournal>()) }
    var refresh by remember { mutableStateOf(0) }

    LaunchedEffect(refresh) { journal = loadJournal() }

    val headers = listOf("Datum", "Uhrzeit", "Typ", "Build", "Benutzer")
    Div({ classes(SystemverwaltungStyles.screenRoot) }) {
        Div({ classes(SystemverwaltungStyles.title) }) { Text("Build-Journal") }
        Div({ classes(SystemverwaltungStyles.tableArea) }) {
            TabularScreen(
                screenKey = "SysBuildJournal",
                rollenKey = "SystemBuildJournal",
                title = "Build-Journal",
                headers = headers,
                data = journal.mapIndexed { index, entry ->
                    (entry.id.ifBlank { "journal_$index" }) to
                        listOf(entry.date, entry.time, entry.type, entry.version, entry.user)
                },
                editable = false,
                hardReadOnly = true,
                includeMetadata = false,
                smartMode = false,
                nonEditableCols = headers.toSet(),
                allowCreate = false,
                allowSave = false,
                allowDelete = false,
                allowImportButton = false,
                allowExportButton = true,
                onRefresh = { refresh++ },
                onCommit = {}
            )
        }
    }
}

@Composable
fun SysNodeAuskunftScreen() {
    Style(SystemverwaltungStyles)
    var nodes by remember { mutableStateOf<List<SystemNodeInfo>>(emptyList()) }
    var selectedNodeId by remember { mutableStateOf("") }

    LaunchedEffect(Unit) {
        while (true) {
            val loaded = NodeUebersichtApi.loadNodes()
                .filterNot { it.environment.equals("test", ignoreCase = true) }
                .sortedWith(compareBy<SystemNodeInfo> { it.typ }.thenBy { it.firmenId })
            nodes = loaded
            if (selectedNodeId.isBlank() || loaded.none { it.id == selectedNodeId }) {
                selectedNodeId = loaded.firstOrNull()?.id.orEmpty()
            }
            delay(1000)
        }
    }

    val selectedNode = nodes.firstOrNull { it.id == selectedNodeId }
    val activeWorkers = selectedNode?.workers.orEmpty()
        .filter { it.healthy }
        .sortedWith(compareBy<org.example.project.manager.SystemNodeWorkerInfo> { !it.preferred }.thenByDescending { it.buildName })
    val nodeHeaders = listOf(
        "Kunde", "Node", "Typ", "Umgebung", "Status", "Port", "Version", "Benutzer",
        "Aktive Worker", "Start", "Laufzeit", "PID", "Lizenz", "Fehler"
    )
    val workerHeaders = listOf(
        "Worker", "Version", "Bevorzugt", "Status", "Benutzer", "Start", "Laufzeit",
        "Node", "Umgebung", "Port", "PID", "Lizenz", "Fehler"
    )

    Div({ classes(SystemverwaltungStyles.screenRoot) }) {
        Div({ classes(SystemverwaltungStyles.title) }) { Text("Node-Auskunft") }
        Div({ classes(SystemverwaltungStyles.tableArea) }) {
            TabularSplitScreen(
                initialRatio = 0.52f,
                minRatio = 0.22f,
                maxRatio = 0.78f,
                screen1 = {
                    Div({ classes(SystemverwaltungStyles.tableArea) }) {
                        TabularScreen(
                            screenKey = "SysNodeAuskunftNodes",
                            rollenKey = "",
                            title = "Nodes",
                            headers = nodeHeaders,
                            data = nodes.map { node ->
                                node.id to listOf(
                                    node.firma.ifBlank { node.firmenId }, node.id, node.typ,
                                    node.environment.uppercase(), node.status, node.port,
                                    node.version.ifBlank { "-" }, node.activeSessions.toString(),
                                    node.workers.count { it.healthy }.toString(), node.startzeit.ifBlank { "-" },
                                    node.laufzeit.ifBlank { "-" }, node.processId.ifBlank { "-" },
                                    node.lizenz.ifBlank { "-" }, node.fehlerCounter.toString()
                                )
                            },
                            editable = false,
                            hardReadOnly = true,
                            includeMetadata = false,
                            smartMode = false,
                            nonEditableCols = nodeHeaders.toSet(),
                            allowCreate = false,
                            allowSave = false,
                            allowDelete = false,
                            allowImportButton = false,
                            allowExportButton = true,
                            onRowClick = { selectedNodeId = it },
                            onRefresh = {},
                            onCommit = {}
                        )
                    }
                },
                screen2 = {
                    Div({ classes(SystemverwaltungStyles.tableArea) }) {
                        TabularScreen(
                            screenKey = "SysNodeAuskunftWorker",
                            rollenKey = "",
                            title = selectedNode?.let { "Worker ${it.id}" } ?: "Worker",
                            headers = workerHeaders,
                            data = activeWorkers.mapIndexed { index, worker ->
                                "${selectedNodeId}_${worker.buildName}_$index" to listOf(
                                    "Worker ${index + 1}", worker.buildName.removePrefix("build_").ifBlank { "-" },
                                    if (worker.preferred) "Ja" else "Nein",
                                    if (worker.healthy) "ONLINE" else "OFFLINE",
                                    worker.activeSessions.toString(), worker.startzeit.ifBlank { "-" },
                                    worker.laufzeit.ifBlank { "-" }, selectedNode?.id.orEmpty(),
                                    selectedNode?.environment?.uppercase().orEmpty(), selectedNode?.port.orEmpty(),
                                    selectedNode?.processId?.ifBlank { "-" }.orEmpty(),
                                    selectedNode?.lizenz?.ifBlank { "-" }.orEmpty(),
                                    selectedNode?.fehlerCounter?.toString().orEmpty()
                                )
                            },
                            editable = false,
                            hardReadOnly = true,
                            includeMetadata = false,
                            smartMode = false,
                            nonEditableCols = workerHeaders.toSet(),
                            allowCreate = false,
                            allowSave = false,
                            allowDelete = false,
                            allowImportButton = false,
                            allowExportButton = true,
                            onRefresh = {},
                            onCommit = {}
                        )
                    }
                }
            )
        }
    }
}
